import type { DesktopHandoffRouteFamily } from './constants';

import { CUBIC_WEB_ORIGIN, MAX_HANDOFF_URL_LENGTH } from './constants';

import { isDevelopment } from '@/lib/env';

type HandoffLocation = Pick<URL, 'origin' | 'pathname' | 'search' | 'hash'>;

const ENCODED_PATH_SEPARATOR = /%(?:2f|5c)/i;
const MALFORMED_PERCENT_ESCAPE = /%(?![0-9a-f]{2})/i;
const SENSITIVE_QUERY_PARAM_KEY_PATTERN =
  /token|password|secret|apikey|credential|authorization|authcode|oauth|clientid|cookie|jwt|bearer|(?:user|account|installation|organization|org|workspace)(?:ids?)?|emails?|sessions?/;

/** The hashes a handoff target may carry, by route family, matched against the decoded hash. */
export const HANDOFF_HASH_PATTERNS: Record<DesktopHandoffRouteFamily, RegExp> = {
  inbox: /^notifications$/,
  pr: /^(?:\d+|discussion(?:-|_r)\d+|pr-v2-(?:discussion-thread-comment|file-thread-comment|inline-comment)-\d+(?:-content)?)$/,
};

function getPathAndSuffix(raw: string): { path: string; suffix: string } {
  const queryIndex = raw.indexOf('?');
  const hashIndex = raw.indexOf('#');
  const suffixIndex = [queryIndex, hashIndex]
    .filter((index) => index >= 0)
    .reduce((earliest, index) => Math.min(earliest, index), raw.length);

  return {
    path: raw.slice(0, suffixIndex),
    suffix: raw.slice(suffixIndex),
  };
}

function decodeAndValidatePath(rawPath: string): string | null {
  let decodedPath = rawPath;

  while (true) {
    if (ENCODED_PATH_SEPARATOR.test(decodedPath)) {
      return null;
    }

    let nextPath: string;
    try {
      nextPath = decodeURIComponent(decodedPath);
    } catch {
      return null;
    }

    if (nextPath.includes('\\')) {
      return null;
    }

    const segments = nextPath.split('/');
    if (segments.some((segment) => segment === '.' || segment === '..')) {
      return null;
    }

    if (nextPath === decodedPath) {
      return nextPath;
    }
    decodedPath = nextPath;
  }
}

function normalizeQueryParamKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function hasSensitiveQueryParam(searchParams: URLSearchParams): boolean {
  return [...searchParams.keys()].some((key) => {
    const normalizedKey = normalizeQueryParamKey(key);
    return (
      normalizedKey === 'code' ||
      normalizedKey === 'state' ||
      normalizedKey === 'auth' ||
      normalizedKey === 'authentication' ||
      SENSITIVE_QUERY_PARAM_KEY_PATTERN.test(normalizedKey)
    );
  });
}

function hasAllowedHash(routeFamily: DesktopHandoffRouteFamily, hash: string): boolean {
  if (!hash) {
    return true;
  }

  let decodedHash: string;
  try {
    decodedHash = decodeURIComponent(hash.slice(1));
  } catch {
    return false;
  }

  return HANDOFF_HASH_PATTERNS[routeFamily].test(decodedHash);
}

export function getDesktopHandoffRouteFamily(pathname: string): DesktopHandoffRouteFamily | null {
  if (pathname === '/inbox') {
    return 'inbox';
  }

  const segments = pathname.split('/');
  const isPullRequest =
    segments.length >= 6 &&
    segments[1] === 'pr' &&
    Boolean(segments[2]) &&
    Boolean(segments[3]) &&
    segments[4] === 'pull' &&
    /^\d+$/.test(segments[5]!);

  return isPullRequest ? 'pr' : null;
}

/** The route family of a handoff target, read from its fully decoded path as validation reads it. */
export function getHandoffTargetRouteFamily(target: string): DesktopHandoffRouteFamily | null {
  const decodedPath = decodeAndValidatePath(getPathAndSuffix(target).path);
  return decodedPath ? getDesktopHandoffRouteFamily(decodedPath) : null;
}

export function validateHandoffTarget(raw: unknown): string | null {
  if (
    typeof raw !== 'string' ||
    raw.length === 0 ||
    raw.length > MAX_HANDOFF_URL_LENGTH ||
    !raw.startsWith('/') ||
    raw.startsWith('//') ||
    raw.includes('\\') ||
    MALFORMED_PERCENT_ESCAPE.test(raw)
  ) {
    return null;
  }

  const { path: rawPath, suffix } = getPathAndSuffix(raw);
  const routeFamily = getHandoffTargetRouteFamily(raw);
  if (!routeFamily) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(raw, CUBIC_WEB_ORIGIN);
  } catch {
    return null;
  }

  if (
    parsed.origin !== CUBIC_WEB_ORIGIN ||
    parsed.username ||
    parsed.password ||
    parsed.pathname !== rawPath ||
    hasSensitiveQueryParam(parsed.searchParams) ||
    !hasAllowedHash(routeFamily, parsed.hash)
  ) {
    return null;
  }

  return `${rawPath}${suffix}`;
}

export function getEligibleHandoffTarget(location: HandoffLocation): string | null {
  const isAllowedOrigin =
    location.origin === CUBIC_WEB_ORIGIN ||
    (isDevelopment() && location.origin === 'http://localhost:3000');
  if (!isAllowedOrigin) {
    return null;
  }

  if (location.pathname === '/sign-in') {
    const returnTo = new URLSearchParams(location.search).get('returnTo');
    return validateHandoffTarget(returnTo);
  }

  return validateHandoffTarget(`${location.pathname}${location.search}${location.hash}`);
}
